// Neo-Riemannian PLR transformations on the 24 major/minor triads.
//
// P (parallel), L (leittonwechsel) and R (relative) each move exactly ONE voice
// — by a semitone (P, L) or a whole tone (R) — and each is an involution
// (applying it twice returns the same triad). Together they generate a dihedral
// group of order 24: from any triad, chains of L and R alone reach all 24
// (Beethoven famously rides such an LR-chain in the Ninth). On the Tonnetz
// lattice the three moves are the three edge-flips of a triad's triangle.

import type { PitchClass } from './notes';

export type TriadQuality = 'major' | 'minor';
export type PLRMove = 'P' | 'L' | 'R';

export interface Triad {
  rootPc: PitchClass;
  quality: TriadQuality;
}

const norm = (pc: number): PitchClass => ((pc % 12) + 12) % 12;

/** The triad's pitch classes, low-to-high from the root: root, third, fifth. */
export function triadPcs(t: Triad): [PitchClass, PitchClass, PitchClass] {
  const third = t.quality === 'major' ? 4 : 3;
  return [norm(t.rootPc), norm(t.rootPc + third), norm(t.rootPc + 7)];
}

/** Apply one PLR move. Each is an involution; each moves exactly one voice. */
export function applyPLR(t: Triad, move: PLRMove): Triad {
  const major = t.quality === 'major';
  switch (move) {
    case 'P':
      // C ↔ Cm — the third slips a semitone; root and fifth hold.
      return { rootPc: t.rootPc, quality: major ? 'minor' : 'major' };
    case 'L':
      // C → Em (root falls to the leading tone) / Em → C.
      return major
        ? { rootPc: norm(t.rootPc + 4), quality: 'minor' }
        : { rootPc: norm(t.rootPc + 8), quality: 'major' };
    case 'R':
      // C → Am (fifth steps up a tone) / Am → C.
      return major
        ? { rootPc: norm(t.rootPc + 9), quality: 'minor' }
        : { rootPc: norm(t.rootPc + 3), quality: 'major' };
  }
}

/**
 * The hexatonic pole (P·L·P — equivalently L·P·L): the "uncanny" partner four
 * single-semitone moves away that shares NO common tone (C major ↔ A♭ minor).
 * Richard Cohn's reading of its use for the supernatural in film and late
 * Romantic music.
 */
export function hexatonicPole(t: Triad): Triad {
  return applyPLR(applyPLR(applyPLR(t, 'P'), 'L'), 'P');
}

/** How many pitch classes two triads share (P/L neighbours: 2, the pole: 0). */
export function commonTones(a: Triad, b: Triad): number {
  const setA = new Set(triadPcs(a));
  return triadPcs(b).filter((pc) => setA.has(pc)).length;
}

/** Short display name: C, Cm, F♯, F♯m … */
export function triadName(t: Triad, spell: (pc: PitchClass) => string): string {
  return `${spell(t.rootPc)}${t.quality === 'minor' ? 'm' : ''}`;
}

// ---------- The Tonnetz lattice ----------
//
// Node (i, j) carries pitch class 7i + 4j (right = a fifth, up-right = a major
// third). The upward triangle at (i, j) spells the MAJOR triad on that pc; the
// downward triangle the MINOR triad a major third up. P, L and R are then the
// three edge-flips of a triangle — pure lattice geometry.

export interface TonnetzPos {
  i: number;
  j: number;
  /** Upward triangle = major; downward = minor. */
  up: boolean;
}

export function tonnetzPc(i: number, j: number): PitchClass {
  return norm(7 * i + 4 * j);
}

/** The triad a lattice triangle spells. */
export function tonnetzTriad(pos: TonnetzPos): Triad {
  const m = tonnetzPc(pos.i, pos.j);
  return pos.up ? { rootPc: m, quality: 'major' } : { rootPc: norm(m + 4), quality: 'minor' };
}

/** Edge-flip a triangle: the lattice image of applyPLR (tested equivalent). */
export function tonnetzMove(pos: TonnetzPos, move: PLRMove): TonnetzPos {
  if (pos.up) {
    if (move === 'P') return { i: pos.i, j: pos.j - 1, up: false };
    if (move === 'L') return { i: pos.i, j: pos.j, up: false };
    return { i: pos.i - 1, j: pos.j, up: false }; // R
  }
  if (move === 'P') return { i: pos.i, j: pos.j + 1, up: true };
  if (move === 'L') return { i: pos.i, j: pos.j, up: true };
  return { i: pos.i + 1, j: pos.j, up: true }; // R
}
